import React, { useMemo } from "react";
import { Bar } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisLeft } from "@visx/axis";
import { scaleLinear, scaleBand } from "@visx/scale";
import { useChartDimensions } from "../../../../hooks/useChartDimensions";

// Helper function to format percentage
const formatPercentage = (value) => {
  return `${Math.round(value * 100)}%`;
};

const BarChart = ({
  data,
  width = 400,
  height = 300,
  margin = { top: 20, right: 120, bottom: 20, left: 200 },
  title = undefined,
  colors = ["#157787"],
  className = "",
}) => {
  const [containerRef, dimensions] = useChartDimensions({
    width,
    height,
    marginTop: margin.top,
    marginRight: margin.right,
    marginBottom: margin.bottom,
    marginLeft: margin.left,
  });

  const { boundedWidth, boundedHeight, margin: chartMargin } = dimensions;

  // Get all responses from the data
  const responses = useMemo(() => {
    return Object.entries(data.responses || {}).map(([label, value]) => ({
      label,
      value: typeof value === "number" ? value : value.value,
    }));
  }, [data]);

  // Calculate max value
  const maxValue = useMemo(() => {
    return Math.max(...responses.map((r) => r.value));
  }, [responses]);

  const xScale = useMemo(() => {
    return scaleLinear({
      range: [0, boundedWidth],
      domain: [0, maxValue],
      nice: true,
    });
  }, [boundedWidth, maxValue]);

  const yScale = useMemo(() => {
    return scaleBand({
      range: [0, boundedHeight],
      domain: responses.map((r) => r.label),
      padding: 0.3,
    });
  }, [responses, boundedHeight]);

  return (
    <div>
      {title && <div className="title">{title}</div>}
      <div ref={containerRef} className={className}>
        <svg width={width} height={height}>
          <Group top={chartMargin?.top} left={chartMargin?.left}>
            {/* Render bars */}
            {responses.map((response, i) => {
              const barWidth = xScale(response.value);
              const barHeight = yScale.bandwidth();
              const barY = yScale(response.label);

              if (!barY) return null;

              return (
                <Group key={`bar-${i}`}>
                  <Bar
                    x={0}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={colors[i % colors.length]}
                    rx={4}
                  />
                  {/* Percentage label */}
                  <text
                    x={barWidth + 8}
                    y={barY + barHeight / 2}
                    dy=".32em"
                    fontSize={16}
                    fontFamily="Sofia Sans Extra Condensed"
                    fill="#333333"
                    textAnchor="start"
                  >
                    {formatPercentage(response.value)}
                  </text>
                </Group>
              );
            })}

            {/* Render y-axis */}
            <AxisLeft
              scale={yScale}
              stroke="#333333"
              tickStroke="#333333"
              hideTicks
              tickLabelProps={() => ({
                fill: "#333333",
                fontSize: 16,
                textAnchor: "end",
                fontFamily: "Sofia Sans Extra Condensed",
                dy: "0.33em",
                dx: -8,
              })}
            />
          </Group>
        </svg>
      </div>
    </div>
  );
};

export default BarChart;
